import { Injectable, OnDestroy, computed, signal } from '@angular/core';
import { PageModel, Question } from '@empathy/survey-core';

@Injectable({ providedIn: 'root' })
export class SurveyViewerStateService implements OnDestroy {
  readonly isMediaComplete$ = computed(() => {
    const required = this.requiredMediaIds$();
    if (required.size === 0) {
      return true;
    }

    const completed = this.completedMediaIds$();
    return Array.from(required).every((id) => completed.has(id));
  });

  readonly isViewTimeComplete$ = computed(() => {
    const pageId = this.activePageId$();
    return this.viewTimeCompletedPageIds$().has(pageId);
  });

  readonly remainingViewTime$ = computed(() => {
    const required = this.minViewTimeSeconds$();
    const elapsed = this.minViewElapsedSeconds$();
    return Math.max(0, Math.ceil(required - elapsed));
  });

  readonly canProceed$ = computed(() => this.isMediaComplete$() && this.isViewTimeComplete$());

  private readonly activePageId$ = signal<string | null>(null);

  private readonly requiredMediaIds$ = signal<Set<string>>(new Set());
  private readonly completedMediaIds$ = signal<Set<string>>(new Set());

  private readonly minViewTimeSeconds$ = signal<number>(0);
  private readonly minViewElapsedSeconds$ = signal<number>(0);

  private readonly viewTimeCompletedPageIds$ = signal<Set<string>>(new Set());

  private intervalId: number | null = null;
  private lastTickMs = 0;

  ngOnDestroy(): void {
    this.stopTimer();
  }

  mediaRequiresCompletion(mediaId: string): boolean {
    return this.requiredMediaIds$().has(mediaId);
  }

  setActivePage(page: PageModel): void {
    this.activePageId$.set(page.id);

    this.setRequiredMedia(page.visibleQuestions);
    this.setMinViewTimeForPage(page.visibleQuestions);
  }

  markMediaCompleted(mediaId: string): void {
    const next = new Set(this.completedMediaIds$());
    next.add(mediaId);
    this.completedMediaIds$.set(next);
  }

  private setRequiredMedia(questions: Question[]): void {
    const requiredMediaIds = questions
      .filter((q) => q.requireMediaCompletionInd)
      .flatMap((q) => q.stimulus ?? [])
      .filter((s) => s.type.startsWith('video/') || s.type.startsWith('audio/'))
      .map((s) => s.mediaId);

    this.requiredMediaIds$.set(new Set(requiredMediaIds));
  }

  private setMinViewTimeForPage(questions: Question[]): void {
    const pageId = this.activePageId$();

    if (this.viewTimeCompletedPageIds$().has(pageId)) {
      this.stopTimer();
      this.minViewTimeSeconds$.set(0);
      this.minViewElapsedSeconds$.set(0);
      return;
    }

    const required = questions.reduce((max, q) => Math.max(max, q.minViewTimeSeconds ?? 0), 0);

    this.stopTimer();
    this.minViewTimeSeconds$.set(required);
    this.minViewElapsedSeconds$.set(0);

    if (required <= 0) {
      this.markViewTimeCompleted(pageId);
      return;
    }

    this.startTimer();
  }

  private markViewTimeCompleted(pageId: string): void {
    const next = new Set(this.viewTimeCompletedPageIds$());
    next.add(pageId);
    this.viewTimeCompletedPageIds$.set(next);
  }

  private startTimer(): void {
    this.lastTickMs = Date.now();
    this.intervalId = window.setInterval(() => {
      const now = Date.now();
      const deltaSec = (now - this.lastTickMs) / 1000;
      this.lastTickMs = now;

      const required = this.minViewTimeSeconds$();
      const next = Math.min(required, this.minViewElapsedSeconds$() + deltaSec);
      this.minViewElapsedSeconds$.set(next);

      if (next >= required) {
        const pageId = this.activePageId$();
        if (pageId) {
          this.markViewTimeCompleted(pageId);
        }
        this.stopTimer();
      }
    }, 250);
  }

  private stopTimer(): void {
    if (this.intervalId != null) {
      window.clearInterval(this.intervalId);
    }
    this.intervalId = null;
    this.lastTickMs = 0;
  }
}
